import React, { useEffect, useMemo } from 'react'

const colorSchemes = {
  violet: {
    active: 'border-violet-600 bg-violet-600 text-white',
    hover: 'hover:bg-violet-50',
  },
  emerald: {
    active: 'border-emerald-600 bg-emerald-600 text-white',
    hover: 'hover:bg-emerald-50',
  },
  indigo: {
    active: 'border-indigo-600 bg-indigo-600 text-white',
    hover: 'hover:bg-indigo-50',
  },
  red: {
    active: 'border-red-600 bg-red-600 text-white',
    hover: 'hover:bg-red-50',
  },
  blue: {
    active: 'border-blue-400 bg-blue-400 text-white',
    hover: 'hover:bg-blue-50'
  },
  purple: {
    active: 'border-purple-400 bg-purple-400 text-white',
    hover: 'hover:bg-purple-50'
  }
}

function getPageNumbers(currentPage, totalPages, maxVisible) {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  let visible = new Set(Array.from({ length: totalPages }, (_, i) => i + 1))
  const alwaysKeep = new Set([1, totalPages, currentPage])
  let count = totalPages

  let divisor = 2
  while (count > maxVisible) {
    const toRemove = Math.max(1, Math.ceil(count / divisor))
    const candidates = [...visible].filter(p => !alwaysKeep.has(p)).sort((a, b) => a - b)
    if (candidates.length === 0) break

    const midIdx = Math.floor(candidates.length / 2)
    const halfRemove = Math.floor(toRemove / 2)
    const startIdx = Math.max(0, midIdx - halfRemove)
    const endIdx = Math.min(candidates.length, startIdx + toRemove)

    for (let i = startIdx; i < endIdx; i++) {
      visible.delete(candidates[i])
    }

    count = visible.size
    divisor *= 2
  }

  const sorted = [...visible].sort((a, b) => a - b)
  const result = []
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] !== sorted[i - 1] + 1) {
      result.push('...')
    }
    result.push(sorted[i])
  }
  return result
}

const Pagination = ({ totalPages, currentPage, onPageChange, colorScheme = 'violet', maxVisiblePages = 15 }) => {
  const colors = colorSchemes[colorScheme] || colorSchemes.violet
  const pages = useMemo(() => getPageNumbers(currentPage, totalPages, maxVisiblePages), [currentPage, totalPages, maxVisiblePages])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [currentPage])

  if (totalPages <= 1) return null

  return (
    <nav aria-label="Pagination" className="mt-10 flex flex-wrap items-center justify-center gap-1">
      <button
        type="button"
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        aria-label="Previous page"
        className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Previous
      </button>

      {pages.map((page, index) =>
        page === '...' ? (
          <span
            key={`ellipsis-${index}`}
            aria-hidden="true"
            className="flex h-10 w-10 items-center justify-center text-sm text-gray-400 select-none"
          >
            ...
          </span>
        ) : (
          <button
            key={page}
            type="button"
            onClick={() => onPageChange(page)}
            aria-current={page === currentPage ? 'page' : undefined}
            aria-label={`Page ${page}`}
            className={`rounded-lg border px-4 py-2 text-sm font-medium transition ${
              page === currentPage
                ? colors.active
                : `border-gray-300 bg-white text-gray-700 ${colors.hover}`
            }`}
          >
            {page}
          </button>
        )
      )}

      <button
        type="button"
        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
        aria-label="Next page"
        className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Next
      </button>
    </nav>
  )
}

export default Pagination
